import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Archive, MapPin, Boxes, Grid3X3, Settings, Package, ArrowRight } from "lucide-react";
import MobileNav from "./MobileNav";
import { useSiteAuth } from "./SiteAuth";

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, requestAccess } = useSiteAuth();
  
  // Check if current page has a subheader
  const hasSubheader = ["/block-builder", "/ctm-slicer", "/asset-tools", "/world-builder", "/topo-import", "/admin", "/resource-pack"].includes(location.pathname);
  
  // Check if on landing page or public pages (hide tool nav)
  const isLandingPage = location.pathname === "/";
  const isPublicPage = ["/", "/privacy", "/terms", "/legal", "/faq", "/contact"].includes(location.pathname);

  // Public pages render without the app chrome (they have their own layout)
  // /admin also opts out — the CMS now uses its own full-screen shell.
  if ((isPublicPage && !isLandingPage) || location.pathname === "/admin") {
    return <>{children}</>;
  }

  // Handle protected navigation
  const handleProtectedNav = (e, path) => {
    if (!isAuthenticated) {
      e.preventDefault();
      requestAccess(() => navigate(path));
    }
  };

  return (
    <div className="app-content">
      <header className="header-bar" style={isLandingPage ? { 
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: "rgba(0,0,0,0.3)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)"
      } : {}}>
        <div 
          className="logo-section" 
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          <div className="logo-placeholder" style={{ width: 28, height: 28 }}>
            <img src="/logo.webp" alt="LumenForge" style={{ width: "100%", height: "100%" }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: "0.5px" }}>LumenForge</div>
            <div style={{ fontSize: 9, color: "#888", marginTop: -1 }}>Advanced Minecraft Tooling</div>
          </div>
          {/* Divider - hidden on mobile via CSS */}
          <div className="desktop-only" style={{ width: 1, height: 24, background: "rgba(255,255,255,0.15)", margin: "0 8px" }} />
          {/* Prototype badge - hidden on mobile via CSS */}
          <span className="desktop-only" style={{ fontSize: 11, fontWeight: 600, color: "#4ade80", letterSpacing: "1px" }}>PROTOTYPE</span>
        </div>
        
        {/* Spacer to push nav to the right */}
        <div style={{ flex: 1 }} />
        
        {isLandingPage ? (
          /* Landing page: simplified nav */
          <nav className="nav-tabs desktop-only" style={{ gap: 4 }}>
            <button
              data-testid="landing-enter-app"
              onClick={() => {
                if (!isAuthenticated) {
                  requestAccess(() => navigate("/block-builder"));
                } else {
                  navigate("/block-builder");
                }
              }}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 18px", borderRadius: 6,
                background: "#f59e0b", border: "none",
                color: "#000", fontSize: 12, fontWeight: 700,
                cursor: "pointer", transition: "all 0.2s"
              }}
            >
              Enter the App <ArrowRight size={14} />
            </button>
            <NavLink
              to="/admin"
              className={({ isActive }) => `nav-tab admin-tab ${isActive ? "active" : ""}`}
              onClick={(e) => handleProtectedNav(e, "/admin")}
              style={() => ({
                background: "transparent",
                borderColor: "transparent",
                minWidth: "auto",
                padding: "8px 12px"
              })}
            >
              <Settings size={16} style={{ color: "#49ad9d" }} />
            </NavLink>
          </nav>
        ) : (
          /* App pages: full tool nav */
          <>
            <nav className="nav-tabs desktop-only">
          <NavLink
            to="/block-builder"
            className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
            data-testid="nav-block-builder"
            onClick={(e) => handleProtectedNav(e, "/block-builder")}
          >
            <Boxes size={14} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
            Block Builder
          </NavLink>
          <NavLink
            to="/ctm-slicer"
            className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
            data-testid="nav-ctm-slicer"
            onClick={(e) => handleProtectedNav(e, "/ctm-slicer")}
          >
            <Grid3X3 size={14} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
            Connected Textures Slicer
          </NavLink>
          <NavLink
            to="/world-builder"
            className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
            data-testid="nav-world-builder"
            onClick={(e) => handleProtectedNav(e, "/world-builder")}
          >
            <MapPin size={14} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
            World Builder
          </NavLink>
          <NavLink
            to="/asset-tools"
            className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
            data-testid="nav-asset-tools"
            onClick={(e) => handleProtectedNav(e, "/asset-tools")}
          >
            <Archive size={14} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
            Asset Tools
          </NavLink>
          <NavLink
            to="/resource-pack"
            className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
            data-testid="nav-resource-pack"
            onClick={(e) => handleProtectedNav(e, "/resource-pack")}
          >
            <Package size={14} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
            Resource Pack
          </NavLink>
          <NavLink
            to="/admin"
            className={({ isActive }) => `nav-tab admin-tab ${isActive ? "active" : ""}`}
            data-testid="nav-admin"
            onClick={(e) => handleProtectedNav(e, "/admin")}
            style={({ isActive }) => ({
              background: isActive ? "rgba(73, 173, 157, 0.2)" : "transparent",
              borderColor: isActive ? "rgba(73, 173, 157, 0.5)" : "transparent",
              minWidth: "auto",
              padding: "8px 12px"
            })}
          >
            <Settings size={16} style={{ color: "#49ad9d" }} />
          </NavLink>
          </nav>
          </>
        )}

        {/* Mobile Navigation (Burger Menu) */}
        <MobileNav />
      </header>
      <main className={`main-content ${hasSubheader ? "with-subheader" : ""} ${isLandingPage ? "landing-page" : ""}`}>{children}</main>
    </div>
  );
}
